<script lang="ts">
import { defineComponent, PropType } from 'vue';
import ControlsContainer from 'dive-common/components/ControlsContainer.vue';
import TrackSettingsPanel from 'dive-common/components/TrackSettingsPanel.vue';
import TrackListColumnSettings from 'dive-common/components/TrackListColumnSettings.vue';
import TrackList from 'vue-media-annotator/components/Tracks/TrackList.vue';
import FilterList from 'vue-media-annotator/components/Types/FilterList.vue';
import TypeSettingsPanel from 'dive-common/components/Types/TypeSettingsPanel.vue';
import ConfidenceFilter from 'dive-common/components/ConfidenceFilter.vue';
import ConfidenceSubsection from 'dive-common/components/ConfidenceSubsection.vue';
import AttributeSubsection from 'dive-common/components/Attributes/AttributesSubsection.vue';
import context from 'dive-common/store/context';
import type { Attribute } from 'vue-media-annotator/use/AttributeTypes';
import type Track from 'vue-media-annotator/track';
import type StyleManager from 'vue-media-annotator/StyleManager';
import type TrackFilterControls from 'vue-media-annotator/TrackFilterControls';
import type { AnnotationSettings } from 'dive-common/store/settings';
import type { DatasetType } from 'dive-common/apispec';
import { useCameraStore, useTrackFilters } from 'vue-media-annotator/provides';

export default defineComponent({
  name: 'BottomPanel',
  components: {
    ControlsContainer,
    TrackList,
    TrackSettingsPanel,
    TrackListColumnSettings,
    FilterList,
    TypeSettingsPanel,
    ConfidenceFilter,
    ConfidenceSubsection,
    AttributeSubsection,
  },
  props: {
    sidebarMode: { type: String, required: true },
    controlsRef: { type: null, required: true },
    controlsCollapsed: { type: Boolean, required: true },
    lineChartData: { type: Array as PropType<unknown[]>, required: true },
    eventChartData: { type: Object as PropType<Record<string, unknown>>, required: true },
    groupChartData: { type: Object as PropType<Record<string, unknown>>, required: true },
    datasetType: { type: String as PropType<DatasetType>, required: true },
    isDefaultImage: { type: Boolean, required: true },
    clientSettings: { type: Object as PropType<AnnotationSettings>, required: true },
    trackFilters: { type: Object as PropType<TrackFilterControls>, required: true },
    attributes: { type: Array as PropType<Attribute[]>, required: true },
    frameRate: { type: Number, required: true },
    readonlyState: { type: Boolean, required: true },
    disableAnnotationFilters: { type: Boolean, required: true },
    promptVisible: { type: Function as PropType<() => boolean>, required: true },
    confidenceFilters: { type: Object as PropType<Record<string, number>>, required: true },
    aggregateSeek: { type: Function as PropType<(frame: number) => void>, required: true },
    trackStyleManager: { type: Object as PropType<StyleManager>, required: true },
    bottomRightPanelView: { type: String, required: true },
    toggleBottomRightPanel: { type: Function as PropType<() => void>, required: true },
    selectedTrackForDetails: { type: Object as PropType<Track | null>, default: null },
    showConfidenceFirst: { type: Boolean, required: true },
    showTrackAttributesFirst: { type: Boolean, required: true },
    editIndividual: { type: Object as PropType<Attribute | null>, default: null },
    setEditIndividual: {
      type: Function as PropType<(attribute: Attribute | null) => void>,
      required: true,
    },
    resetEditIndividual: {
      type: Function as PropType<(event: MouseEvent) => void>,
      required: true,
    },
    addAttribute: {
      type: Function as PropType<(mode: 'Track' | 'Detection') => void>,
      required: true,
    },
    editAttribute: { type: Function as PropType<(attribute: Attribute) => void>, required: true },
    saveThreshold: { type: Function as PropType<() => void>, required: true },
    isStereoDataset: { type: Boolean, default: false },
  },
  setup(props) {
    const cameraStore = useCameraStore();
    const trackFilters = useTrackFilters();
    function acceptTrackType(type: string) {
      if (props.selectedTrackForDetails) {
        cameraStore.acceptTrackType(
          props.selectedTrackForDetails.id,
          type,
          trackFilters.hierarchyIndex.value,
        );
      }
    }
    return { acceptTrackType, context };
  },
});
</script>

<template>
  <div
    class="d-flex flex-shrink-0"
    :style="{
      'border-top': '1px solid #444',
      height: sidebarMode === 'bottom' ? '260px' : 'auto',
    }"
  >
    <div
      class="d-flex flex-column bottom-panel-section"
      :style="{
        width: sidebarMode === 'bottom' ? '28%' : '100%',
        'min-width': '0',
        overflow: 'hidden',
      }"
    >
      <ControlsContainer
        :ref="controlsRef"
        bottom-layout
        :wrap-bottom-controls="sidebarMode === 'bottom'"
        :collapsed.sync="controlsCollapsed"
        v-bind="{
          lineChartData, eventChartData, groupChartData, datasetType, isDefaultImage,
        }"
      />
    </div>

    <div
      v-if="sidebarMode === 'bottom'"
      class="d-flex flex-column bottom-panel-section"
      :style="{ width: '44%', 'min-width': '0', overflow: 'hidden' }"
    >
      <TrackList
        class="fill-height"
        compact
        :new-track-mode="clientSettings.trackSettings.newTrackSettings.mode"
        :new-track-type="clientSettings.trackSettings.newTrackSettings.type"
        :lock-types="clientSettings.typeSettings.lockTypes"
        :hotkeys-disabled="promptVisible() || readonlyState"
        :height="220"
        :fps="frameRate"
        :disabled="disableAnnotationFilters"
        @track-seek="aggregateSeek($event)"
      >
        <template slot="settings">
          <TrackSettingsPanel
            :all-types="trackFilters.allTypes.value"
            :is-stereo-dataset="isStereoDataset"
          />
        </template>
        <template slot="column-settings">
          <TrackListColumnSettings
            :attributes="attributes.filter((attr) => attr.belongs === 'track')"
            :fps="frameRate"
          />
        </template>
      </TrackList>
    </div>

    <div
      v-if="sidebarMode === 'bottom'"
      class="d-flex flex-column bottom-panel-section"
      :style="{ width: '28%', 'min-width': '0', overflow: 'hidden' }"
    >
      <div class="right-panel-header d-flex align-center px-2 py-1">
        <span class="right-panel-title">
          {{ bottomRightPanelView === 'filters' ? 'Type Filters' : 'Track Details' }}
        </span>
        <v-spacer />
        <v-tooltip bottom>
          <template #activator="{ on }">
            <v-btn
              icon
              x-small
              v-on="on"
              @click="toggleBottomRightPanel()"
            >
              <v-icon small>
                mdi-swap-horizontal
              </v-icon>
            </v-btn>
          </template>
          <span>{{ bottomRightPanelView === 'filters' ? 'Switch to Track Details' : 'Switch to Type Filters' }}</span>
        </v-tooltip>
      </div>

      <template v-if="bottomRightPanelView === 'filters'">
        <div class="bottom-filter-list">
          <FilterList
            :show-empty-types="clientSettings.typeSettings.showEmptyTypes"
            :height="130"
            :width="300"
            :header-height="50"
            :style-manager="trackStyleManager"
            :filter-controls="trackFilters"
            :disabled="disableAnnotationFilters"
            class="flex-grow-1"
          >
            <template #settings>
              <TypeSettingsPanel
                :all-types="trackFilters.allTypes.value"
                :hierarchy-active="trackFilters.hierarchyActive.value"
              />
            </template>
          </FilterList>
        </div>
        <div class="confidence-row-bottom px-2 py-1">
          <ConfidenceFilter
            :confidence.sync="confidenceFilters.default"
            :disabled="disableAnnotationFilters"
            text="Confidence"
            @end="saveThreshold()"
          >
            <a
              style="text-decoration: underline; color: white;"
              @click="context.toggle('TypeThreshold')"
            >
              Advanced
            </a>
          </ConfidenceFilter>
        </div>
      </template>

      <template v-else>
        <div
          class="flex-grow-1 bottom-details-panel"
          style="overflow-y: auto; overflow-x: hidden;"
          @click="resetEditIndividual"
        >
          <div v-if="selectedTrackForDetails" class="pa-2">
            <ConfidenceSubsection
              v-if="showConfidenceFirst"
              :confidence-pairs="selectedTrackForDetails.confidencePairs"
              :disabled="false"
              @set-type="acceptTrackType($event)"
            />
            <template v-if="showTrackAttributesFirst">
              <AttributeSubsection
                mode="Track"
                :attributes="attributes"
                :edit-individual="editIndividual"
                @edit-attribute="editAttribute($event)"
                @set-edit-individual="setEditIndividual($event)"
                @add-attribute="addAttribute"
              />
              <AttributeSubsection
                mode="Detection"
                :attributes="attributes"
                :edit-individual="editIndividual"
                @edit-attribute="editAttribute($event)"
                @set-edit-individual="setEditIndividual($event)"
                @add-attribute="addAttribute"
              />
            </template>
            <template v-else>
              <AttributeSubsection
                mode="Detection"
                :attributes="attributes"
                :edit-individual="editIndividual"
                @edit-attribute="editAttribute($event)"
                @set-edit-individual="setEditIndividual($event)"
                @add-attribute="addAttribute"
              />
              <AttributeSubsection
                mode="Track"
                :attributes="attributes"
                :edit-individual="editIndividual"
                @edit-attribute="editAttribute($event)"
                @set-edit-individual="setEditIndividual($event)"
                @add-attribute="addAttribute"
              />
            </template>
            <ConfidenceSubsection
              v-if="!showConfidenceFirst"
              :confidence-pairs="selectedTrackForDetails.confidencePairs"
              :disabled="false"
              @set-type="acceptTrackType($event)"
            />
          </div>
          <div v-else class="pa-3 text-caption grey--text">
            No track selected. Select a track to view its type classifications and attributes.
          </div>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped lang="scss">
.bottom-panel-section {
  background-color: #1e1e1e;
  border: 1px solid #555;
  border-radius: 4px;
  margin: 4px;
}

.confidence-row-bottom {
  background-color: #262626;
  border-top: 1px solid #444;
  flex-shrink: 0;
  padding-top: 4px !important;
  padding-bottom: 4px !important;

  .text-body-2 {
    font-size: 14px !important;
    font-weight: 600;
    color: white !important;
  }
}

.bottom-filter-list {
  display: flex;
  flex-direction: column;
  flex: 1 1 0;
  min-height: 0;
  overflow: hidden;
  #type-header b {
    font-size: 14px;
    font-weight: 600;
    color: white;
  }
}

.right-panel-header {
  background-color: #262626;
  border-bottom: 1px solid #444;
  flex-shrink: 0;
  min-height: 28px;
}

.right-panel-title {
  font-size: 14px;
  font-weight: 600;
  color: white;
}
</style>
